<template>
  <div>
    <v-card class="rounded-lg" flat>
      <v-container fluid class="pa-7">
        <v-row>
          <v-col>
            <div class="d-flex justify-space-between align-center">
              <div
                class="font-weight-medium text-h5 text-start textLight--text"
              >
                {{
                  isCreate
                    ? $t("user.subRoles.addNewRole")
                    : $t("user.subRoles.updateRole")
                }}
              </div>
              <v-switch
                style="width: 40px"
                class="ma-0"
                flat
                dense
                hide-details
                inset
                color="success"
                v-model="subRole.isActive"
              >
              </v-switch>
            </div>
          </v-col>
        </v-row>

        <v-row class="textLight--text">
          <ValidationObserver ref="observer">
            <v-col cols="12">
              <v-row align="center">
                <v-col cols="3">
                  <div class="text-start font-weight-regular text-subtitle-2">
                    {{ $t("user.subRoles.roleName") }}
                  </div>
                </v-col>
                <v-col cols="9">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <v-text-field
                      :readonly="isImmunableName"
                      :error-messages="errors"
                      @click="registerErr = null"
                      class="rounded-lg"
                      v-model="subRole.name"
                      dense
                      hide-details
                      outlined
                      @input="subRole.name = subRole.name.toUpperCase()"
                    ></v-text-field>
                  </validation-provider>
                </v-col>

                <v-col cols="3">
                  <div class="text-start font-weight-regular text-subtitle-2">
                    {{ $t("user.subRoles.permissions") }}
                  </div>
                </v-col>
                <v-col cols="9">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <v-autocomplete
                      :error-messages="errors"
                      @click="registerErr = null"
                      dense
                      hide-details
                      v-model="subRole.permissions"
                      :items="
                        subRole.name &&
                        subRole.name.toUpperCase() === 'ORGANIZATION_MANAGER'
                          ? assignableOrganizationPermissions
                          : permissionsArr
                      "
                      flat
                      solo
                      background-color="#e6e6e680"
                      multiple
                      class="rounded-lg"
                      :filter="customLocalisedAutoCompleteFilter"
                    >
                      <template slot="selection" slot-scope="data">
                        <v-chip
                          close
                          text-color="white"
                          style="max-width: 320px; font-size: 0.875rem; font-weight: 500;"
                          color="#281162"
                          class="me-1 mt-1 rounded-lg py-1 px-3"
                          @click:close="deleteChip(data.item)"
                        >
                          <div class="text-truncate">
                            {{ data.item }}
                          </div>
                        </v-chip>
                      </template>
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item
                          v-on="on"
                          v-bind="attrs"
                          #default="{ active }"
                        >
                          <v-checkbox
                            :input-value="active"
                            :label="item"
                          ></v-checkbox>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </validation-provider>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" v-if="registerErr">
              <p class="pa-0 ma-0 error--text">
                {{ registerErr }}
              </p>
            </v-col>
            <v-col cols="12">
              <v-btn
                depressed
                block
                min-height="45"
                color="primary"
                style="text-transform: unset"
                class="rounded-lg font-weight-normal"
                :loading="loading"
                @click="submit"
              >
                {{ isCreate ? $t("organization.add") : $t("update") }}
              </v-btn>
            </v-col>
          </ValidationObserver>
        </v-row>
      </v-container>
    </v-card>
  </div>
</template>
<script>
import "../../core-module/util/validationRules";
import { createOrUpdateSubRole } from "../methods/createOrUpdateSubRole";
import immunableSubRoles from "../assets/json/immunableSubRoles";

export default {
  data() {
    return {
      immunableSubRoles,
      subRole: {},
      permissionsSelected: [],
      loading: false,
      registerErr: null,
      isImmunableName: false,
    };
  },
  props: {
    subRoleData: {
      type: Object,
      default: () => {},
    },
    isCreate: {
      type: Boolean,
      default: true,
    },
    permissionsArr: {
      type: Array,
      default: () => [],
    },
    assignableOrganizationPermissions: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    subRoleData: {
      immediate: true,
      handler() {
        this.subRole = JSON.parse(JSON.stringify(this.subRoleData)) || {};
        this.subRole.isActive = this.isCreate ? true : this.subRole.isActive;
        this.isImmunableName = this.immunableSubRoles.find(
          role => role === this.subRole?.name?.toUpperCase()
        )
          ? true
          : false;
        this.registerErr = null;
        this.setPermissionsSelected();
      },
      deep: true,
    },
  },
  methods: {
    setPermissionsSelected() {
      if (this.subRole.permissions && this.subRole.permissions.length) {
        this.subRole.permissions = this.permissionsArr.filter(permission => {
          return this.subRole.permissions.find(existingPermission => {
            return permission === existingPermission;
          });
        });
      }
    },
    deleteChip(val) {
      if (val) {
        this.subRole.permissions = this.subRole.permissions.filter(
          permission => permission !== val
        );
      }
    },
    async submit() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        let immunableName = this.immunableSubRoles.find(
          role => role === this.subRole?.name?.toUpperCase()
        );

        const permissionList =
          immunableName === "ORGANIZATION_MANAGER"
            ? this.assignableOrganizationPermissions
            : this.permissionsArr;

        this.subRole.permissions = this.subRole.permissions?.filter(
          permission => permissionList.includes(permission)
        );

        if (!this.isImmunableName && immunableName) {
          const msg =
            this.$i18n.locale === "ar"
              ? 'هل أنت متأكد من الاستمرار "' +
                immunableName +
                '"سيتم حفظ هذا في الدور الموجود بالفعل'
              : 'This will be saved to already existing role "' +
                immunableName +
                '". Are you sure to continue?';

          this.$dialog
            .confirm(msg)
            .then(() => {
              this.createOrUpdate();
            })
            .catch(_ => {});
        } else {
          this.createOrUpdate();
        }
      }
    },
    async createOrUpdate() {
      this.loading = true;
      await createOrUpdateSubRole(this.subRole).then(response => {
        this.loading = false;
        if (response.data) {
          this.$emit("close");
          if (!this.subRole._id) this.$emit("afterCreate");
        } else if (response.error) {
          if (response.error.includes("ALREADY_EXIST"))
            this.registerErr = this.$t("inputRules.emailAlreadyExist");
          // else if (result.error.includes("FIELD_REQUIRED"))
          //   this.registerErr = this.$t("emailOrCompanyEmailNeeded");
          else this.registerErr = this.$t("somethingWentWrong");
        }
      });
    },
  },
};
</script>
<style scoped>
>>> .v-select__selections {
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 112px;
  margin-block: 10px;
}
</style>